import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from '@tanstack/react-router';
import { configureAbly, useChannel } from '@ably-labs/react-hooks';
import { useApplicationData } from 'remote-state/applicationHooks';
import { useQueryClient } from '@tanstack/react-query';
import {
  selectActiveView,
  selectColumnsOrder,
  selectDefaultView,
  selectOriginalView,
  setActiveView,
  setColumnsOrder,
  setDefaultView,
  setOriginalView,
} from 'features/queue/slice';
import { getShowNavBar, isMobileUserAgent } from 'common/utils/utils';
import EscalationPrompt from 'common/components/escalationPrompt';
import NavigationBar from 'features/navBar';
import { BASIC_EDITION } from 'features/settings/constants';
import { COMPANY_FIELD_KEY, SESSION_STORAGE } from 'features/queue/constants';
import { ErrorBoundary } from 'common/components/errorBoundary';
import { getViewInitializedColumnConfig } from 'features/queue/utils';
import { details, selectActiveUser } from 'store/userSlice';
import { handleLocalHostUserDetails, isLocalHost } from 'services/localhost';
import { QUERIES_KEYS } from 'constant';
import { AUTH_CONSTANTS } from 'constants/auth';
import { APP_CONSTANTS } from 'constants/app';
import { CHANNEL_MESSAGE_TYPES } from 'constants/common';
import PendingComponentLoader from 'common/components/pendingComponentLoader';
import { toggle } from './slice';
import { StyledContainer } from './style';
import { getUserMobileLink } from '../../services/userService';

async function isMobileUserAgentRedirected(userAgent, userRole) {
  if (isMobileUserAgent(userAgent) && userRole === AUTH_CONSTANTS.ADMIN) {
    const res = await getUserMobileLink();
    if (res?.mobileAppServerUrl?.length > 0) {
      window.location.href = res.mobileAppServerUrl;
      return true;
    }
  }
  return false;
}

const handleBeforeLoad = async ({ currentUser, dispatch }) => {
  if (isLocalHost()) {
    if (currentUser?.isAuth) {
      if (window.permissionInitialized) return;
      window.permissionInitialized = true;
    } else {
      handleLocalHostUserDetails(dispatch);
    }
    return;
  }

  if (currentUser?.roles[0] === AUTH_CONSTANTS.END_USER) {
    window.location.href = '/servicePortal';
    return;
  }

  const isQAMode =
    localStorage.getItem(APP_CONSTANTS.IS_QA_MODE) || localStorage.getItem(APP_CONSTANTS.IS_QA_MODE) === null;

  if (currentUser?.isAuth) {
    window.pendo?.initialize({
      visitor: {
        id: `${localStorage.getItem('account_id')}_${currentUser?.userId}`,
        calculatedRole: currentUser?.calculatedRole,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        deviceMemory: navigator?.deviceMemory,
      },
      account: {
        id: localStorage.getItem('account_id'),
        isSpaces: true,
        isOnDemand: true,
        isQAMode,
      },
    });
    if (window.permissionInitialized) return;
    window.permissionInitialized = true;
  }

  isMobileUserAgentRedirected(navigator.userAgent, currentUser?.roles[0]);
};

const PrivatePagesComponentWrapper = ({ children }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectActiveUser);
  const userDetails = useSelector(details);
  const { isFetched, isFetching } = useApplicationData(QUERIES_KEYS.APPLICATION_DATA, { forceCall: true });
  const showContent = isLocalHost() ? isFetched : isFetched && !isFetching;

  useEffect(() => {
    handleBeforeLoad({ currentUser, userDetails, dispatch });
  }, [currentUser, userDetails, dispatch]);

  return showContent ? <>{children}</> : <PendingComponentLoader />;
};

const accountId = localStorage.getItem(APP_CONSTANTS.ACCOUNT_ID_LOCAL_KEY);
const QUEUE_CHANNEL_NAME = accountId;

export default function LoggedInRoutesPanel({ children }) {
  const dispatch = useDispatch();
  useApplicationData(QUERIES_KEYS.APPLICATION_DATA);
  const router = useRouter();
  const activeView = useSelector(selectActiveView);
  const defaultView = useSelector(selectDefaultView);
  const originalView = useSelector(selectOriginalView);
  const columnsOrder = useSelector(selectColumnsOrder);
  const queryClient = useQueryClient();

  const { data: edition } = useApplicationData(QUERIES_KEYS.EDITION);
  const isBasicEdition = edition === BASIC_EDITION;
  const isLocalEnvironment = isLocalHost();

  configureAbly({
    autoConnect: !isLocalEnvironment, // If working locally with Ably sockets enabled - comment this line out!
    authUrl: '/spaces/api/authentication/websocket/token',
    // authUrl: 'websocket/token', // for dev only
    recover(_, cb) {
      cb(true);
    },
  });

  useChannel(QUEUE_CHANNEL_NAME, ({ name, data }) => {
    console.log('Websocket LoggedIn: useChannel event commited', { name, data });
    if (name === CHANNEL_MESSAGE_TYPES.UPDATE_SETTINGS) {
      //We clean attributes with timeout because Wescoket works much faster than settings changes getting applied on backend.
      //In that case we are not getting updated values on attributes but old one.
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['fieldAttribute'] });
      }, 2000);
    }
  });

  const showNavBar = getShowNavBar(router);

  const toggleNavBarCollapse = () => {
    setTimeout(() => dispatch(toggle()));
  };

  const clearStorageByEdition = useCallback(() => {
    if (columnsOrder) {
      dispatch(setColumnsOrder(columnsOrder.filter((column) => column.fieldName !== COMPANY_FIELD_KEY)));
    }

    let columnsConfig;

    if (defaultView?.columnsConfig) {
      columnsConfig = getViewInitializedColumnConfig(defaultView.columnsConfig);
      dispatch(
        setDefaultView((prev) => ({
          ...prev,
          columnsConfig,
        })),
      );
    }

    if (originalView?.columnsConfig) {
      columnsConfig = getViewInitializedColumnConfig(originalView.columnsConfig);

      dispatch(
        setOriginalView((prev) => ({
          ...prev,
          columnsConfig,
        })),
      );
    }

    if (activeView?.columnsConfig) {
      columnsConfig = getViewInitializedColumnConfig(activeView.columnsConfig);

      dispatch(
        setActiveView((prev) => ({
          ...prev,
          columnsConfig,
        })),
      );
    }
  }, [columnsOrder, activeView, defaultView, originalView, dispatch]);

  useEffect(() => {
    const storedEdition = sessionStorage.getItem(SESSION_STORAGE.edition);
    if (isBasicEdition && (storedEdition === null || Number(edition) !== Number(storedEdition))) {
      clearStorageByEdition();
    }
    if (!storedEdition || Number(edition) !== Number(storedEdition)) {
      sessionStorage.setItem(SESSION_STORAGE.edition, edition);
    }
  }, [edition, isBasicEdition, clearStorageByEdition]);

  // useEffect(() => {
  //   if (userAccount?.username && userAccount?.userId) {
  //     window?.Sentry?.setUser?.({ username: userAccount?.username, id: userAccount?.userId });
  //   }
  // }, [userAccount?.username, userAccount?.userId]);

  return (
    <PrivatePagesComponentWrapper>
      <ErrorBoundary>
        {showNavBar ? (
          <>
            <NavigationBar toggleNavBarCollapse={toggleNavBarCollapse} />
            <StyledContainer>{children}</StyledContainer>
          </>
        ) : (
          { children }
        )}
        <EscalationPrompt />
      </ErrorBoundary>
    </PrivatePagesComponentWrapper>
  );
}
